/**
 * 日期区间的快捷选择（CR-025）。
 *
 * 全部是**纯日历运算**：输入输出都是 `YYYY-MM-DD` 字符串，中途不碰时区。
 * 起点是「工地当地的今天」——那一步由调用方用 `dayKeyInTimeZone` 算好传进来，
 * 之后加减天数、找周一这些事和时区再没有关系。
 *
 * 这样分工的理由：日期字符串一旦进了 `new Date()` 就会被按浏览器时区解析，
 * 管理员在别的时区打开页面，「本周」会错一天。用 `Date.UTC` 做算术、
 * 只取 UTC 字段，结果只取决于传进来的那个日期本身。
 */
/** 一周从周一算起。北美不少排班表从周日起，要改只动这一个常量。 */
export const WEEK_STARTS_ON = 1; // 0 = 周日，1 = 周一
/**
 * 所有快捷选项，按界面上从左到右的顺序。
 *
 * **顺序对 `matchPreset` 有意义**：周一那天「今日」和「本周」解析出同一个区间
 * （本周从周一起、且不算到未来去），谁排在前面谁被判为选中。
 * 放「今日」在前 —— 那天点「今日」的人比点「本周」的多，而且区间本来就一样，
 * 高亮哪个都不会让人看到错的数据。**这不是 bug，别当 bug 改**（CR-031）。
 */
export const RANGE_PRESETS = [
    'today',
    'yesterday',
    'thisWeek',
    'lastWeek',
    'lastTwoWeeks',
    'thisMonth',
];
function parse(key) {
    const [y, m, d] = key.split('-').map(Number);
    return { y: y ?? 1970, m: m ?? 1, d: d ?? 1 };
}
function format(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
function toUtcMs(key) {
    const { y, m, d } = parse(key);
    return Date.UTC(y, m - 1, d);
}
/** 加减天数。`Date.UTC` 会自己处理跨月跨年 */
export function addDays(key, days) {
    return format(toUtcMs(key) + days * 86_400_000);
}
/** 星期几：0 = 周日 … 6 = 周六 */
export function weekdayOf(key) {
    return new Date(toUtcMs(key)).getUTCDay();
}
/** 这一天所在周的第一天 */
export function startOfWeek(key) {
    const shift = (weekdayOf(key) - WEEK_STARTS_ON + 7) % 7;
    return addDays(key, -shift);
}
/** 这一天所在月的第一天 */
export function startOfMonth(key) {
    const { y, m } = parse(key);
    return format(Date.UTC(y, m - 1, 1));
}
/**
 * 把快捷选项解析成具体区间。
 *
 * @param preset 选项
 * @param today  工地当地的今天（`YYYY-MM-DD`）
 *
 * 各选项的口径：
 * - `today`         今天一天
 * - `yesterday`     昨天一天（**不含今天** —— 「昨天干了什么」是个独立的问题）
 * - `thisWeek`      本周第一天 → 今天（**不到未来去**，否则区间里一半是空的）
 * - `lastWeek`      上一个完整周，周一到周日
 * - `lastTwoWeeks`  最近 14 天（含今天）—— 双周结算最常用的那一种
 * - `thisMonth`     本月 1 号 → 今天
 */
export function resolvePreset(preset, today) {
    switch (preset) {
        case 'today':
            return { from: today, to: today };
        case 'yesterday': {
            const y = addDays(today, -1);
            return { from: y, to: y };
        }
        case 'thisWeek':
            return { from: startOfWeek(today), to: today };
        case 'lastWeek': {
            const lastWeekStart = addDays(startOfWeek(today), -7);
            return { from: lastWeekStart, to: addDays(lastWeekStart, 6) };
        }
        case 'lastTwoWeeks':
            return { from: addDays(today, -13), to: today };
        case 'thisMonth':
            return { from: startOfMonth(today), to: today };
    }
}
/**
 * 当前区间正好等于哪个快捷选项。用来把对应的按钮标成选中。
 *
 * 手动改了日期就一个都不匹配，返回 null —— 那时不该有按钮亮着，
 * 否则界面在说谎：显示的区间和高亮的按钮对不上。
 */
export function matchPreset(range, today) {
    for (const preset of RANGE_PRESETS) {
        const candidate = resolvePreset(preset, today);
        if (candidate.from === range.from && candidate.to === range.to)
            return preset;
    }
    return null;
}
//# sourceMappingURL=date-range.js.map